import React, { createContext, useContext, useMemo } from 'react';
import { QueryClientProvider } from 'react-query';

import { QueryClientManager } from 'scbk-services/http/managers/query-client-manager.ts';
import _ from 'scbk-services/utils/lodash-extension.ts';

import type { RestHandler } from 'scbk-services/types/http/rest-handler';

interface HttpContextProps {
    baseUrl: string;
    onRestDefaultError?: RestHandler.ErrorHandler;
    onRestForbiddenHandler?: RestHandler.ForbiddenHandler;
    onRestUnauthorizedError?: RestHandler.UnauthorizedHandler;
}

interface HttpProviderProps {
    baseUrl: string;
    children: React.ReactNode;
    onRestDefaultError?: RestHandler.ErrorHandler;
    onRestForbiddenHandler?: RestHandler.ForbiddenHandler;
    onRestUnauthorizedError?: RestHandler.UnauthorizedHandler;
}

const HttpContext = createContext<HttpContextProps>({ baseUrl: '' });

const useHttpContext = () => {
    const context = useContext(HttpContext);

    if (_.isEmpty(context)) {
        throw new Error(
            'useService must be used within ServiceProvider, please make sure that your component is wrapped with <ServiceContextProvider> to access the service configuration',
        );
    }

    return context;
};

const HttpProvider: React.FC<HttpProviderProps> = ({
    baseUrl,
    children,
    onRestDefaultError,
    onRestForbiddenHandler,
    onRestUnauthorizedError,
}) => {
    const parentContext = useContext(HttpContext);

    const contextValue = useMemo(
        () => ({
            baseUrl: _.isNotEmpty(parentContext?.baseUrl) ? parentContext?.baseUrl : baseUrl,
            onRestDefaultError: _.isNotNil(parentContext?.onRestDefaultError)
                ? parentContext?.onRestDefaultError
                : onRestDefaultError,
            onRestForbiddenHandler: _.isNotNil(parentContext?.onRestForbiddenHandler)
                ? parentContext?.onRestForbiddenHandler
                : onRestForbiddenHandler,
            onRestUnauthorizedError: _.isNotNil(parentContext?.onRestUnauthorizedError)
                ? parentContext?.onRestUnauthorizedError
                : onRestUnauthorizedError,
        }),
        [
            baseUrl,
            onRestDefaultError,
            onRestForbiddenHandler,
            onRestUnauthorizedError,
            parentContext?.baseUrl,
            parentContext?.onRestDefaultError,
            parentContext?.onRestForbiddenHandler,
            parentContext?.onRestUnauthorizedError,
        ],
    );

    const queryClient = QueryClientManager.getQueryClient();

    return (
        <HttpContext.Provider value={contextValue}>
            <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>
            {/* <QueryClientProvider client={queryClient}>
                {children}
                <ReactQueryDevtools initialIsOpen={false} />
            </QueryClientProvider> */}
        </HttpContext.Provider>
    );
};

export { HttpProvider, useHttpContext };
export type { HttpProviderProps };
